import { Router } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { Component, OnInit, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { selectCurrentPlatform } from 'src/app/data/store/selectors/platform.selectors';
import { availableImageFormats, befreeUrlValues, camera as cameraIcon } from '../../constants';
import { CurrentPlatform, FileType, ImageProcessingProfile, SpinnerColor } from '../../enums';
import { ModalController, IonicModule } from '@ionic/angular';
import { ImageCropperComponent } from '../image-cropper/image-cropper.component';
import { ICroppedImageData } from 'src/app/data/store/models/uploading-files.model';
import { addUserAvatar } from 'src/app/data/store/actions/users.actions';
import {
  clearUploadedFilesData,
  initUploadFile,
} from 'src/app/data/store/actions/uploading-files.actions';
import {
  selectCurrentUser,
  selectUserAvatarIsLoading,
} from 'src/app/data/store/selectors/users.selectors';
import {
  selectLocalId,
  selectUploadingFilesData,
  selectUploadingFilesIsLoading,
} from 'src/app/data/store/selectors/uploading-files.selectors';
import { PostsService } from '../../services/posts/posts.service';
import { NgClass } from '@angular/common';
import { SpinnerComponent } from '../spinner/spinner.component';

const selectAvatarImageLoading = createSelector(
  selectUserAvatarIsLoading,
  selectUploadingFilesIsLoading,
  (avatarLoading: boolean, uploadingLoading: boolean) => avatarLoading || uploadingLoading,
);

@UntilDestroy()
@Component({
  selector: 'app-avatar-picker',
  templateUrl: './avatar-picker.component.html',
  styleUrls: ['./avatar-picker.component.scss'],
  imports: [NgClass, IonicModule, SpinnerComponent],
})
export class AvatarPickerComponent implements OnInit {
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _postsService = inject(PostsService);
  private readonly _modalCtrl = inject(ModalController);

  localId = -1;

  isDataLoading = false;

  isCheckLoading = false;

  private isAvatarUploadPending = false;

  currentPlatform: CurrentPlatform | null = null;

  public readonly SpinnerColor = SpinnerColor;

  public readonly CurrentPlatform = CurrentPlatform;

  public readonly cameraIcon = cameraIcon;

  public readonly availableImageFormats = availableImageFormats;

  public currentUser$ = this._store.select(selectCurrentUser);

  public uploadingImages$ = this._store.select(selectUploadingFilesData);

  ngOnInit(): void {
    this._store
      .select(selectLocalId)
      .pipe(untilDestroyed(this))
      .subscribe((id: number) => {
        this.localId = id;
      });
    this._store
      .select(selectCurrentPlatform)
      .pipe(untilDestroyed(this))
      .subscribe((platform) => {
        this.currentPlatform = platform;
      });
    this._store
      .select(selectAvatarImageLoading)
      .pipe(untilDestroyed(this))
      .subscribe((isLoading) => {
        this.isDataLoading = isLoading;
      });

    this.currentUser$?.pipe(untilDestroyed(this)).subscribe((user) => {
      const isAvatarPage = new RegExp(
        `/${befreeUrlValues.dashboard}/${befreeUrlValues.avatar}`,
      ).test(this._router.url);
      if (user?.avatar && isAvatarPage) {
        this._router.navigate([befreeUrlValues.dashboard, befreeUrlValues.posts]);
      }
    });

    this.uploadingImages$?.pipe(untilDestroyed(this)).subscribe((fileData) => {
      const uploadedFile = fileData[0];
      if (this.isAvatarUploadPending && uploadedFile?.uploadedFileData) {
        this.isAvatarUploadPending = false;
        this.saveAvatar(uploadedFile.uploadedFileData.id);
        this._store.dispatch(clearUploadedFilesData());
      }
    });
  }

  private saveAvatar(fileId: number) {
    setTimeout(() => {
      const filePipe$ = this._postsService.getFile(fileId);
      filePipe$.pipe(untilDestroyed(this)).subscribe((file) => {
        if (file.loaded) {
          this.isCheckLoading = false;
          this._store.dispatch(
            addUserAvatar({
              avatar: {
                originalImage: { id: fileId },
              },
            }),
          );
        } else {
          this.saveAvatar(fileId);
        }
      });
    }, 1000);
  }

  private onImageCropped(data: ICroppedImageData) {
    this.isCheckLoading = true;
    this.isAvatarUploadPending = true;
    this._store.dispatch(
      initUploadFile({
        localId: this.localId,
        fileType: FileType.image,
        file: data.file,
        imageProcessingProfile: ImageProcessingProfile.avatar,
      }),
    );
  }

  private async openCropper(file: File) {
    const modal = await this._modalCtrl.create({
      component: ImageCropperComponent,
      backdropDismiss: false,
      componentProps: {
        closed: () => modal.dismiss(),
        changed: (data: ICroppedImageData) => this.onImageCropped(data),
        imageFile: file,
        aspectRatio: 1,
      },
    });
    modal.present();
  }

  public handleImageInputChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.openCropper(file);
    }
    // Фикс, чтобы была возможность открыть один и тот же файл несколько раз подряр
    (event.target as HTMLInputElement).value = '';
  }

  get isLoading() {
    return this.isDataLoading || this.isCheckLoading;
  }
}
